import { workshopAppointmentDocumentsService } from '@/api/workshop-appointment-documents.service';
import { removeItemFromQueriesData } from '@/utils/update-query-data';
import type {
  CreateWorkshopAppointmentDocumentDto,
  WorkshopAppointmentDocumentDto,
  WorkshopAppointmentsControllerFindAllWorkshopAppointmentDocumentsData,
} from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';

const QUERY_KEY = 'workshop-appointment-documents';

export const useWorkshopAppointmentDocument = (
  workshopAppointmentId: string,
  documentId: string,
  enabled?: boolean,
) =>
  useQuery({
    queryKey: [QUERY_KEY, workshopAppointmentId, documentId],
    queryFn: () => workshopAppointmentDocumentsService.getOne(workshopAppointmentId, documentId),
    enabled,
  });

export const useWorkshopAppointmentDocumentsWithQuery = (
  workshopAppointmentId: string,
  query: WorkshopAppointmentsControllerFindAllWorkshopAppointmentDocumentsData['query'],
) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEY, workshopAppointmentId, query],
    queryFn: ({ pageParam = 1 }) =>
      workshopAppointmentDocumentsService.getAllPaginated(workshopAppointmentId, {
        ...query,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });

export const useCreateWorkshopAppointmentDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      workshopAppointmentId,
      document,
    }: {
      workshopAppointmentId: string;
      document: CreateWorkshopAppointmentDocumentDto;
    }) => workshopAppointmentDocumentsService.create(workshopAppointmentId, document),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Workshop appointment document created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.workshopAppointmentId],
      });
    },
  });
};

export const useRemoveWorkshopAppointmentDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      workshopAppointmentId,
      documentId,
    }: {
      workshopAppointmentId: string;
      documentId: string;
    }) => workshopAppointmentDocumentsService.remove(workshopAppointmentId, documentId),
    onMutate: ({ workshopAppointmentId, documentId }) => ({
      workshopAppointmentId,
      documentId,
      successMessage: 'Workshop appointment document deleted',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<WorkshopAppointmentDocumentDto>(
        [QUERY_KEY, variables.workshopAppointmentId],
        variables.documentId,
        queryClient,
      );
    },
  });
};

export const useUpdateWorkshopAppointmentDocument = () => {
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      workshopAppointmentId,
      documentId,
      document,
    }: {
      workshopAppointmentId: string;
      documentId: string;
      document: WorkshopAppointmentDocumentDto;
    }) => workshopAppointmentDocumentsService.update(workshopAppointmentId, documentId, document),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Workshop appointment document updated',
      invalidateQueryKeys: [QUERY_KEY],
    }),
  });
};
